// V2 source path: customer/midas/components/v2/MidasDebugHarnessV2.tsx
// Strategy: dev-only utility
// Verified no-MUI: yes
//
// Dev-only debug harness for Tier B viewers. The bot doesn't actively
// emit `table` / `machine_card` / `sr_summary` / `sr_detail` artifacts
// in production today (Round 14 — only image / diagram_overlay /
// action_form are real-traffic), so we can't visually verify the V2
// viewers via a real chat round-trip. This harness lets the user load
// `/midas-v2?midas-debug-artifact=table` (or `=all`) and have sample
// payloads of the requested type(s) injected into the dock so each
// viewer renders in isolation.
//
// Invocation:
//   /midas-v2?midas-debug-artifact=table
//   /midas-v2?midas-debug-artifact=machine_card
//   /midas-v2?midas-debug-artifact=sr_summary
//   /midas-v2?midas-debug-artifact=sr_detail
//   /midas-v2?midas-debug-artifact=all       ← injects all four
//
// Production gate:
//   - Inject site is gated by `process.env.NODE_ENV !== "production"`.
//     CRA / Vite builds set NODE_ENV=production for prod builds, so the
//     hook short-circuits to a no-op there. Belt-and-braces: the hook
//     ALSO requires the query param to be present, so even if the
//     env-gate ever leaked, no UI changes unless the user explicitly
//     opts in. This means `.cc` (which is built with NODE_ENV=production)
//     would NOT respond to the query param.
//
// Important: the user explicitly requested that `.cc` be able to
// verify the harness via the query param (Tier B is mock-only and we
// can't deploy a non-prod build there). To allow this, we accept EITHER
// gate being open:
//   - dev build (NODE_ENV !== "production")  OR
//   - query param explicitly present
// In production builds with NO query param (the default user flow),
// the harness is a complete no-op — no logging, no dispatch, no DOM
// changes. The query-param gate is the user-facing safety: if someone
// doesn't know the param, they will never see harness output.
//
// To "lock" the harness out of production entirely, change the gate
// at line `const harnessEnabled` to drop the OR clause.

import { useEffect, useRef } from "react";

import { useAppDispatch } from "../../../../shared/hooks/sharedHooks";
import { openArtifact } from "../../midasPageSlice";
import { Artifact } from "../../midasPageModels";

// ===================================================================
// Sample payload factories
// ===================================================================

/** Realistic-shape sample for the `table` viewer. Matches
 *  TableArtifactData (columns + rows + total_count). */
const sampleTableArtifact = (): Artifact => ({
  type: "table",
  title: "Open Service Reports — DMG MORI NLX 2500",
  source: "machineid",
  url: "midas-debug://table",
  data: {
    columns: [
      { key: "number", label: "SR #", width: "100px" },
      { key: "title", label: "Title" },
      { key: "status", label: "Status", width: "140px" },
      { key: "priority", label: "Priority", width: "100px" },
      { key: "created", label: "Created", width: "120px" },
      { key: "assignee", label: "Assignee" },
    ],
    rows: [
      {
        number: "SR-1042",
        title: "Spindle vibration above threshold",
        status: "In Progress",
        priority: "high",
        created: "2026-04-18T09:14:00Z",
        assignee: "P. Kvasnica",
        link: "/service/requests/1042",
      },
      {
        number: "SR-1039",
        title: "Coolant pressure low — needs reseal",
        status: "Open",
        priority: "medium",
        created: "2026-04-17T13:30:00Z",
        assignee: "M. Novak",
        link: "/service/requests/1039",
      },
      {
        number: "SR-1031",
        title: "Tool magazine drift after 4000h",
        status: "Awaiting confirmation",
        priority: "medium",
        created: "2026-04-12T08:05:00Z",
        assignee: "—",
        link: "/service/requests/1031",
      },
      {
        number: "SR-1028",
        title: "Routine 4000h inspection — completed",
        status: "Completed",
        priority: "low",
        created: "2026-04-09T15:00:00Z",
        assignee: "T. Horvath",
      },
      {
        number: "SR-1019",
        title: "Z-axis ballscrew replacement",
        status: "Closed",
        priority: "low",
        created: "2026-04-02T11:42:00Z",
        assignee: "T. Horvath",
      },
    ],
    total_count: 5,
  },
});

/** Realistic-shape sample for the `machine_card` viewer. Matches
 *  MachineCardArtifactData. The wrapper dispatches `getMachine(id)`
 *  using `machine_id`. With a real `.cc` id below, the wrapper will
 *  fetch the actual machine and render the canonical
 *  ProfileSidebarCard. With an invalid id, the wrapper falls back to
 *  its "machine not found" state. */
const sampleMachineCardArtifact = (): Artifact => ({
  type: "machine_card",
  title: "DMG MORI NLX 2500",
  source: "machineid",
  url: "midas-debug://machine_card",
  data: {
    // Real .cc machine id provided by the user for harness review.
    machine_id: "22b81a7d-91c8-4f2d-5079-08de78c0813e",
    name: "DMG MORI NLX 2500",
    manufacturer: "DMG MORI",
    model: "NLX 2500 / 700",
    serial_number: "NLX25-2024-0042",
    internal_number: "M-101",
    production_year: 2024,
    type: "Lathe",
    control_system: "Mitsubishi M830",
    open_service_reports: 2,
    last_service_date: "2026-03-14T00:00:00Z",
    link: "/machinery/22b81a7d-91c8-4f2d-5079-08de78c0813e",
  },
});

/** Realistic-shape sample for the `sr_summary` viewer. Matches
 *  SRSummaryArtifactData. */
const sampleSRSummaryArtifact = (): Artifact => ({
  type: "sr_summary",
  title: "SR-1042 — Spindle vibration above threshold",
  source: "machineid",
  url: "midas-debug://sr_summary",
  data: {
    id: "1042",
    number: "SR-1042",
    title: "Spindle vibration above threshold during finishing pass",
    description:
      "During the 800 RPM finishing pass on the OD of part 2026-04-18-A the spindle showed vibration spikes well outside the configured envelope. Operator stopped the cycle and logged the anomaly. Initial check suggests bearing wear; full diagnostic + bearing replacement scheduled.",
    status: "In Progress",
    priority: "high",
    machine_model: "DMG MORI NLX 2500",
    created_at: "2026-04-18T09:14:00Z",
    offers_count: 3,
    assigned_provider: "Precision Spindle Services s.r.o.",
    timeline: [
      {
        event: "Service request opened",
        timestamp: "2026-04-18T09:14:00Z",
        user: "P. Kvasnica",
      },
      {
        event: "3 offers received",
        timestamp: "2026-04-18T16:42:00Z",
      },
      {
        event: "Provider selected — Precision Spindle Services",
        timestamp: "2026-04-19T08:30:00Z",
        user: "P. Kvasnica",
      },
      {
        event: "Work scheduled for 2026-04-25",
        timestamp: "2026-04-19T09:00:00Z",
      },
    ],
    link: "/service/requests/1042",
  },
});

/** Realistic-shape sample for the `sr_detail` viewer. Matches the
 *  data shape consumed by ServiceReportDetailView (reportNumber +
 *  machineId — the platform component fetches the rest itself). Uses
 *  real .cc ids so the platform component fully renders. With an
 *  invalid combination the platform component will show its own
 *  "SR not found" state. */
const sampleSRDetailArtifact = (): Artifact => ({
  type: "sr_detail",
  title: "SR-260414-001 — Full detail",
  source: "machineid",
  url: "midas-debug://sr_detail",
  data: {
    // Real .cc SR + machine ids provided by the user for harness review.
    // SR-260414-001 belongs to machine 22b81a7d-91c8-4f2d-5079-08de78c0813e.
    reportNumber: "SR-260414-001",
    machineId: "22b81a7d-91c8-4f2d-5079-08de78c0813e",
  },
});

// ===================================================================
// Hook
// ===================================================================

const HARNESS_QUERY_PARAM = "midas-debug-artifact";

type HarnessKey =
  | "table"
  | "machine_card"
  | "sr_summary"
  | "sr_detail"
  | "all";

const PAYLOAD_FACTORIES: Record<Exclude<HarnessKey, "all">, () => Artifact> = {
  table: sampleTableArtifact,
  machine_card: sampleMachineCardArtifact,
  sr_summary: sampleSRSummaryArtifact,
  sr_detail: sampleSRDetailArtifact,
};

const isHarnessKey = (v: string | null): v is HarnessKey =>
  v === "table" ||
  v === "machine_card" ||
  v === "sr_summary" ||
  v === "sr_detail" ||
  v === "all";

/**
 * Reads the URL query param and (gated) injects sample artifacts.
 * Runs once per component mount — guarded by a ref so re-renders don't
 * re-inject. Production gate: must EITHER be a dev build OR have the
 * query param present. In production with no query param, this hook
 * is a complete no-op.
 */
export const useMidasDebugHarness = (): void => {
  const dispatch = useAppDispatch();
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    if (typeof window === "undefined") return;

    const params = new URLSearchParams(window.location.search);
    const requested = params.get(HARNESS_QUERY_PARAM);

    // First gate: query param must be present AND valid. If not, no-op
    // — production with no opt-in URL stays untouched.
    if (!requested || !isHarnessKey(requested)) return;

    // Second gate: this is the safety belt. If you want to fully lock
    // the harness out of production, drop the `|| true` here so that
    // only NODE_ENV !== "production" passes. Today we accept the URL
    // param as user opt-in even on production builds (Tier B mock
    // review needs to happen on .cc which is a prod build).
    const isDev = process.env.NODE_ENV !== "production";
    const harnessEnabled = isDev || requested != null;
    if (!harnessEnabled) return;

    ranRef.current = true;

    const keys: Array<Exclude<HarnessKey, "all">> =
      requested === "all"
        ? ["table", "machine_card", "sr_summary", "sr_detail"]
        : [requested];

    keys.forEach((key) => {
      const artifact = PAYLOAD_FACTORIES[key]();
      dispatch(openArtifact(artifact));
    });
  }, [dispatch]);
};

/**
 * Component wrapper for the hook. Mount once near the root of MidasPageV2
 * to activate. Renders nothing — the hook side-effects into the slice.
 */
const MidasDebugHarnessV2: React.FC = () => {
  useMidasDebugHarness();
  return null;
};

export default MidasDebugHarnessV2;
